/* PublicSamWidget — floating chatbot on marketing pages.
 *
 * - Hidden when the visitor is authenticated.
 * - On first click: pre-chat form (name, email, optional company).
 * - On submit: opens chat panel and starts an SSE stream against
 *   POST /api/public/sam/message.
 * - 429 → "Sam's catching her breath" banner.
 * - session_id is persisted in sessionStorage (per-tab) so reloads in the
 *   same tab keep the same conversation, but new tabs start fresh.
 */
import { useEffect, useRef, useState } from "react";
import { MessageSquare, Send, X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "sas:public-sam";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function loadSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function saveSession(s) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

export function PublicSamWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("form");   // 'form' | 'chat'
  const [visitor, setVisitor] = useState({ name: "", email: "", company: "" });
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);       // {role, content}
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  // Iter 27.1 — set when the LLM proxy reports a budget/quota error so the
  // visitor sees a clean "Sam is taking a break" banner instead of the raw
  // litellm.BadRequestError text leaking onto the page.
  const [samUnavailable, setSamUnavailable] = useState(false);
  const scrollRef = useRef(null);

  // Restore previous session (per-tab) so reload doesn't drop the conversation.
  useEffect(() => {
    const prev = loadSession();
    if (prev?.session_id) {
      setSessionId(prev.session_id);
      setVisitor((v) => ({ ...v, email: prev.email || v.email, name: prev.name || v.name, company: prev.company || v.company }));
      if (Array.isArray(prev.messages) && prev.messages.length) {
        setMessages(prev.messages);
        setPhase("chat");
      }
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  // Don't render anything for logged-in users — they have the real Sam in-app.
  if (user) return null;

  const persist = (next) => {
    saveSession({
      session_id: next.sessionId ?? sessionId,
      email: next.email ?? visitor.email,
      name: next.name ?? visitor.name,
      company: next.company ?? visitor.company,
      messages: next.messages ?? messages,
    });
  };

  const submitForm = (e) => {
    e.preventDefault();
    if (!visitor.email.trim() || !visitor.name.trim()) return;
    setPhase("chat");
    setMessages([{ role: "assistant", content: "Hi — I'm Sam. What would you like to know about Symplee — pricing, how it works, or something specific?" }]);
    persist({ messages: [{ role: "assistant", content: "Hi — I'm Sam. What would you like to know about Symplee — pricing, how it works, or something specific?" }] });
  };

  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg || streaming) return;
    setInput("");
    setStreaming(true);
    setRateLimited(false);
    setSamUnavailable(false);
    const nextMessages = [...messages, { role: "user", content: msg }, { role: "assistant", content: "" }];
    setMessages(nextMessages);

    // Iter 27.1 — friendly fallback shown when the model proxy is out of
    // budget / quota. Keeps the visitor's path open via email.
    const SAM_UNAVAILABLE_FALLBACK =
      "Sam is taking a quick break — please email founders@symplee.ai with your question and the team will get back to you within one business day.";
    const isBudgetOrQuotaError = (s) => {
      const t = (s || "").toLowerCase();
      return (
        t.includes("budget has been exceeded") ||
        t.includes("max budget") ||
        t.includes("quota") ||
        t.includes("insufficient_quota") ||
        t.includes("rate limit") ||
        t.includes("ratelimit")
      );
    };

    try {
      const res = await fetch(`${BACKEND_URL}/api/public/sam/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId || null,
          message: msg,
          visitor: {
            name: visitor.name,
            email: visitor.email,
            company: visitor.company || null,
          },
        }),
      });
      if (res.status === 429) {
        setRateLimited(true);
        setMessages((m) => m.slice(0, -1));   // drop the empty assistant turn
        setStreaming(false);
        return;
      }
      if (!res.ok || !res.body) {
        const errText = (await res.text().catch(() => "")) || `HTTP ${res.status}`;
        setMessages((m) => {
          const out = [...m];
          out[out.length - 1] = { role: "assistant", content: `Something went wrong: ${errText}. Try again in a moment.` };
          return out;
        });
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";
      let newSessionId = sessionId;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(trimmed.slice(5).trim());
            if (payload.type === "meta" && payload.session_id) {
              newSessionId = payload.session_id;
              setSessionId(payload.session_id);
            } else if (payload.type === "token") {
              assistantText += payload.content || "";
              setMessages((m) => {
                const out = [...m];
                out[out.length - 1] = { role: "assistant", content: assistantText };
                return out;
              });
            } else if (payload.type === "error") {
              const errStr = payload.error || "";
              if (isBudgetOrQuotaError(errStr)) {
                // Replace the empty assistant bubble with the clean fallback
                // and surface a banner so visitors know to email instead.
                assistantText = SAM_UNAVAILABLE_FALLBACK;
                setSamUnavailable(true);
                setMessages((m) => {
                  const out = [...m];
                  out[out.length - 1] = { role: "assistant", content: assistantText };
                  return out;
                });
              } else {
                assistantText += "\n\n[Sam ran into an error. Try again.]";
              }
            }
          } catch { /* ignore parse errors */ }
        }
      }
      const finalMessages = nextMessages.slice(0, -1).concat({ role: "assistant", content: assistantText || "(no reply)" });
      setMessages(finalMessages);
      persist({ sessionId: newSessionId, messages: finalMessages });
    } catch (err) {
      setMessages((m) => {
        const out = [...m];
        out[out.length - 1] = { role: "assistant", content: "Couldn't reach Sam. Check your connection and try again." };
        return out;
      });
    } finally {
      setStreaming(false);
    }
  };

  const onEnter = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Floating bubble (closed state)
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="public-sam-bubble"
        className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-2 rounded-full border border-white/10 bg-indigo-600 px-5 py-3 text-sm font-medium text-white shadow-[0_18px_40px_-10px_rgba(79,70,229,0.55)] transition hover:bg-indigo-500 dark:bg-white dark:text-slate-900 dark:hover:bg-zinc-100"
      >
        <MessageSquare className="h-4 w-4" />
        Have a question? Ask Sam
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Ask Sam"
      data-testid="public-sam-panel"
      className="fixed bottom-5 right-5 z-[60] flex h-[min(640px,80vh)] w-[min(420px,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a] text-white shadow-[0_30px_80px_-10px_rgba(0,0,0,0.6)]"
    >
      <div className="flex items-center justify-between border-b border-white/8 bg-gradient-to-r from-indigo-500/15 via-violet-500/10 to-fuchsia-500/15 px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10">
            <MessageSquare className="h-3.5 w-3.5 text-indigo-300" />
          </div>
          <div>
            <div className="font-display text-sm">Sam · Symplee</div>
            <div className="text-[10px] uppercase tracking-wider text-white/40">Pre-sales · we respond personally</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="public-sam-close"
          className="rounded-full p-1.5 text-white/50 hover:bg-white/5 hover:text-white"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {phase === "form" ? (
        <form onSubmit={submitForm} className="flex flex-1 flex-col gap-3 overflow-y-auto p-5 text-sm" data-testid="public-sam-form">
          <p className="text-white/70">
            Quick intro so we can follow up if Sam can't answer something here.
            Takes 10 seconds. No spam.
          </p>
          <label className="space-y-1">
            <span className="text-xs uppercase tracking-wider text-white/45">Your name *</span>
            <input
              required
              value={visitor.name}
              onChange={(e) => setVisitor({ ...visitor, name: e.target.value })}
              data-testid="public-sam-name"
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-indigo-400/60"
              placeholder="Jane Doe"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs uppercase tracking-wider text-white/45">Work email *</span>
            <input
              required
              type="email"
              value={visitor.email}
              onChange={(e) => setVisitor({ ...visitor, email: e.target.value })}
              data-testid="public-sam-email"
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-indigo-400/60"
              placeholder="jane@doe.co"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs uppercase tracking-wider text-white/45">Company (optional)</span>
            <input
              value={visitor.company}
              onChange={(e) => setVisitor({ ...visitor, company: e.target.value })}
              data-testid="public-sam-company"
              className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-indigo-400/60"
              placeholder="Doe & Co"
            />
          </label>
          <p className="text-xs text-white/40">By starting a chat you agree we'll keep your email so the team can follow up. We never share it. Unsubscribe whenever.</p>
          <button
            type="submit"
            data-testid="public-sam-start"
            className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-medium text-black transition hover:bg-zinc-200"
          >
            Start chatting →
          </button>
        </form>
      ) : (
        <>
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="public-sam-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-indigo-500/20 text-white"
                    : "bg-white/[0.04] text-white/85"
                }`}
                data-testid={`public-sam-msg-${m.role}-${i}`}
              >
                {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="inline h-3.5 w-3.5 animate-spin text-white/50" /> : "")}
              </div>
            ))}
            {rateLimited && (
              <div className="mx-auto rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="public-sam-rate-limit">
                Sam's catching her breath — try again in a minute.
              </div>
            )}
            {samUnavailable && (
              <div className="mx-auto rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="public-sam-unavailable">
                Sam is offline right now. Email{" "}
                <a
                  href="mailto:founders@symplee.ai"
                  className="underline decoration-amber-300/60 underline-offset-2 hover:text-amber-100"
                >
                  founders@symplee.ai
                </a>{" "}
                and the team will reply personally within one business day.
              </div>
            )}
          </div>
          <div className="border-t border-white/8 p-3">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onEnter}
                rows={1}
                placeholder="Ask about pricing, the trial, anything…"
                data-testid="public-sam-input"
                className="max-h-32 min-h-[40px] flex-1 resize-none rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-indigo-400/60"
              />
              <button
                type="button"
                onClick={sendMessage}
                disabled={!input.trim() || streaming}
                data-testid="public-sam-send"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-black transition hover:bg-zinc-200 disabled:opacity-40"
              >
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-2 text-center text-[10px] text-white/30">
              Grounded on our public pricing & objection-handling docs. Sam will offer a team follow-up if she's not sure.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
